import { useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";

import Input from "../../ChildComponents/Input";
import BrandButton from "../../ChildComponents/BrandButton";

import { Rolechema } from "../validation/roleValidation";

import { useUploadImage, useUpdateImages } from "../../store/api/ImagesApi";
import SmartImageUpload from "../../ChildComponents/SmartImageUploader";

const IMAGE_URL = process.env.REACT_APP_IMAGE_URL;

function ImagesForm({ data, editId, onSuccessCreated }) {
  const queryClient = useQueryClient();
  const updateImages = useUpdateImages(editId?._id);
  const [loading, setLoading] = useState(false);
  const [edited, setEdited] = useState(editId ? true : false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm({
    resolver: yupResolver(Rolechema),
  });

  useEffect(() => {
    if (editId) {
      // Edit Mode
      reset({
        title: editId?.title || "",
        videoURL: editId?.videoURL || "",
        discription: editId?.discription || "",
        image: editId?.image ? `${editId.image}` : null,
        topic:
          editId?.topic?.map((t) => ({
            label: t?.name,
            value: t?._id,
          })) || [],
      });
      setEdited(true);
    } else {
      // Create Mode (editId নেই)
      reset({
        title: "",
        videoURL: "",
        discription: "",
        image: null,
        topic: [],
      });
      setEdited(false);
    }
  }, [editId, reset]);

  const uploadImage = useUploadImage();

  const mutation = useMutation({
    mutationFn: (data) => {
      return editId ? updateImages(data) : uploadImage(data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["images"]);
      setLoading(false);
      toast.success(editId ? "Updated Successfully" : "Created Successfully");
      reset();
      onSuccessCreated(1);
    },
    onError: (error) => {
      toast.error(error.message);
      setLoading(false);
    },
  });

  const onSubmit = (formData) => {
    setLoading(true);

    try {
      const data = new FormData();

      if (!formData.image) {
        toast.warning("Image is required!");
        setLoading(false);
        return;
      }

      data.append("title", formData.title);

      data.append("image", formData.image);

      for (let pair of data.entries()) {
        console.log(pair[0] + ": ", pair[1]);
      }

      mutation.mutate(data);
    } catch (error) {
      setLoading(false);
      toast.error(error.message);
    }
  };

  return (
    <div className="container-fluid p-0">
      {/* Header Section */}
      <div
        className="mb-4 p-4 rounded-4 position-relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          boxShadow: "0 10px 30px rgba(102, 126, 234, 0.3)",
        }}
      >
        <div className="d-flex align-items-center">
          <div
            className="d-flex align-items-center justify-content-center rounded-3 me-3"
            style={{
              width: "48px",
              height: "48px",
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
            }}
          >
            <i
              className="material-icons"
              style={{ color: "white", fontSize: "28px" }}
            >
              {editId ? "edit" : "video_library"}
            </i>
          </div>
          <div>
            <h4 className="text-white mb-1 fw-bold">
              {editId ? "Edit images" : "Add New images"}
            </h4>
            <p
              className="text-white mb-0"
              style={{ opacity: 0.9, fontSize: "0.9rem" }}
            >
              {editId ? "Update images" : "Upload a images "}
            </p>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div
          className="p-4 rounded-4 shadow-sm"
          style={{
            background: "white",
            border: "1px solid #e2e8f0",
          }}
        >
          {/* Basic Information Section */}
          <div className="mb-4">
            <div className="d-flex align-items-center mb-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-2 me-2"
                style={{
                  width: "32px",
                  height: "32px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                }}
              >
                <i
                  className="material-icons"
                  style={{ color: "white", fontSize: "18px" }}
                >
                  info
                </i>
              </div>
              <h5 className="mb-0 fw-semibold" style={{ color: "#2d3748" }}>
                Basic Information
              </h5>
            </div>

            <div className="row">
              <div className="col-lg-4 col-md-6">
                <Input
                  icon="title"
                  {...register("title")}
                  error={errors.title?.message}
                  type="text"
                  placeholder="Enter Image title"
                  label="Image Title"
                />
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="mb-4">
            <div className="d-flex align-items-center mb-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-2 me-2"
                style={{
                  width: "32px",
                  height: "32px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                }}
              >
                <i
                  className="material-icons"
                  style={{ color: "white", fontSize: "18px" }}
                >
                  description
                </i>
              </div>
              <h5 className="mb-0 fw-semibold" style={{ color: "#2d3748" }}>
                Content & Media
              </h5>
            </div>

            <div className="row">
              <div className="col-lg-4">
                {/* Featured Image Card */}
                <div
                  className="card shadow-lg border-0 mb-4"
                  style={{ borderRadius: "20px", overflow: "hidden" }}
                >
                  <div
                    className="card-header text-white py-3"
                    style={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    }}
                  >
                    <h5 className="mb-0 text-white d-flex align-items-center">
                      <i className="material-icons me-2">image</i>
                      Featured Image
                    </h5>
                  </div>
                  <div className="card-body p-4">
                    <Controller
                      name="image"
                      control={control}
                      defaultValue={null}
                      render={({ field }) => (
                        <SmartImageUpload
                          multiple={false}
                          limit={1}
                          files={field.value ? [field.value] : []}
                          onSelectFiles={(url) => {
                            console.log(url);
                            field.onChange(url.data[0].image || "");
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="d-flex align-items-center justify-content-between pt-3 border-top">
            <div
              className="d-flex align-items-center text-muted"
              style={{ fontSize: "0.85rem" }}
            >
              <i className="material-icons me-1" style={{ fontSize: "18px" }}>
                schedule
              </i>
              {editId
                ? "Last updated: " + new Date().toLocaleDateString()
                : "Fill all required fields"}
            </div>

            <div className="d-flex gap-2">
              {editId && (
                <button
                  type="button"
                  className="btn btn-light rounded-3 px-4"
                  onClick={() => reset()}
                  style={{
                    border: "1px solid #e2e8f0",
                    transition: "all 0.3s ease",
                  }}
                >
                  <i
                    className="material-icons me-2"
                    style={{ fontSize: "18px", verticalAlign: "middle" }}
                  >
                    refresh
                  </i>
                  Reset
                </button>
              )}

              <BrandButton
                type="submit"
                title={
                  loading
                    ? "Processing..."
                    : editId
                    ? "Update Image"
                    : "Upload Image "
                }
                disabled={loading}
                className="rounded-3 px-4 shadow"
                style={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                  minWidth: "160px",
                }}
              />
            </div>
          </div>
        </div>
      </form>

      <style>{`
       .ql-toolbar {
          border-radius: 12px 12px 0 0 !important;
          border-color: #e2e8f0 !important;
          background: #f8f9fa !important;
          padding: 12px !important;
        }
        
        .ql-container {
          border-radius: 0 0 12px 12px !important;
          border-color: #e2e8f0 !important;
          font-size: 0.95rem !important;
          min-height: 250px !important;
        }

        .ql-editor {
          min-height: 250px !important;
          font-size: 0.95rem !important;
          line-height: 1.6 !important;
        }

        .ql-editor.ql-blank::before {
          color: #a0aec0 !important;
          font-style: normal !important;
        }

        /* Toolbar button hover effects */
        .ql-toolbar button:hover,
        .ql-toolbar .ql-picker-label:hover {
          color: #667eea !important;
          background: rgba(102, 126, 234, 0.1) !important;
          border-radius: 4px !important;
        }

        .ql-toolbar button.ql-active,
        .ql-toolbar .ql-picker-label.ql-active {
          color: #667eea !important;
          background: rgba(102, 126, 234, 0.15) !important;
        }

        /* Color picker styling */
        .ql-picker-options {
          border-radius: 8px !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
        }

        /* Image styling in editor */
        .ql-editor img {
          max-width: 100% !important;
          height: auto !important;
          border-radius: 8px !important;
          margin: 10px 0 !important;
        }

        /* Video styling in editor */
        .ql-editor iframe {
          border-radius: 8px !important;
          margin: 10px 0 !important;
        }

        /* Link styling */
        .ql-editor a {
          color: #667eea !important;
          text-decoration: underline !important;
        }

        /* Code block styling */
        .ql-editor pre.ql-syntax {
          background: #2d3748 !important;
          color: #f7fafc !important;
          border-radius: 8px !important;
          padding: 15px !important;
          margin: 10px 0 !important;
        }

        /* Blockquote styling */
        .ql-editor blockquote {
          border-left: 4px solid #667eea !important;
          padding-left: 16px !important;
          margin-left: 0 !important;
          color: #4a5568 !important;
          font-style: italic !important;
        }

        .btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.12) !important;
        }

        .btn:active {
          transform: translateY(0);
        }
        .ql-toolbar {
          border-radius: 12px 12px 0 0 !important;
          border-color: #e2e8f0 !important;
          background: #f8f9fa !important;
        }
        
        .ql-container {
          border-radius: 0 0 12px 12px !important;
          border-color: #e2e8f0 !important;
          font-size: 0.95rem !important;
        }

        .btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.12) !important;
        }

        .btn:active {
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
}

export default ImagesForm;
